import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatAmountInWords } from '../../../utils/amountInWords';

const CashMemoPreview = ({ data, isLoading }) => {
    const [searchParams] = useSearchParams();
    const preview = searchParams.get('preview');
    const [showPrintModal, setShowPrintModal] = useState(false);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null || amount === '') return '0.00';
        return parseFloat(amount).toFixed(3);
    };

    // Safely extract translated/text values from possible objects like { en: '...' }
    const getText = (value, preferredLang = 'en') => {
        if (!value) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'object') {
            if (value[preferredLang]) return value[preferredLang];
            const first = Object.values(value)[0];
            return typeof first === 'string' ? first : '';
        }
        return String(value);
    };

    useEffect(() => {
        if (!data) return;
        if (preview) setShowPrintModal(true);
    }, [preview, data]);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="text-center p-4">
                <h5>No data available</h5>
            </div>
        );
    }

    const handlePrint = () => {
        setShowPrintModal(true);
    };

    const handleClosePrintModal = () => {
        setShowPrintModal(false);
    };

    return (
        <div className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Cash Memo Details</h5>
                </div>
                <div className="d-flex justify-content-end">
                    <button 
                        className="btn btn-primary btn-sm no-print"
                        onClick={handlePrint}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                        <i className="fas fa-print"></i>
                        Print Preview
                    </button>
                </div>
            </div>

            <div className="flex-grow-1">
                <div className="row">
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Invoice Number:</label>
                            <div className="form-control-plaintext">{data.invoice_number || 'N/A'}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Invoice Date:</label>
                            <div className="form-control-plaintext">{formatDate(data.invoice_date)}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="mb-0">
                            <label className="form-label fw-bold mb-0">Customer:</label>
                            <div className="form-control-plaintext">{data.customer?.label || data.customer_name || 'N/A'}</div>
                        </div>
                    </div>
                </div>

                <div className="table-responsive mt-4">
                    <table className="table table-bordered">
                        <thead>
                            <tr>
                                <th className="text-nowrap p-3">Item Code</th>
                                <th className="text-nowrap p-3">Item Name</th>
                                <th className="text-nowrap p-3">Unit</th>
                                <th className="text-nowrap p-3">Qty</th>
                                <th className="text-nowrap p-3">Unit Price</th>
                                <th className="text-nowrap p-3">Discount %</th>
                                <th className="text-nowrap p-3">Line Amount</th>
                                <th className="text-nowrap p-3">VAT %</th>
                                <th className="text-nowrap p-3">VAT Amt</th>
                                <th className="text-nowrap p-3">Amt Incl VAT</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items && data.items.length > 0 ? (
                                data.items.map((item, index) => (
                                    <tr key={index}>
                                        <td className="text-nowrap p-3 align-middle">{item.item?.item_code || 'N/A'}</td>
                                        <td className="text-nowrap p-3 align-middle">{item.item_description || 'N/A'}</td>
                                        <td className="p-3 align-middle">{item.unit || 'N/A'}</td>
                                        <td className="p-3 align-middle">{item.quantity || '0'}</td>
                                        <td className="p-3 align-middle">{formatCurrency(item.unit_price)}</td>
                                        <td className="p-3 align-middle">{item.discount_percentage || '0.00'}%</td>
                                        <td className="p-3 align-middle">{formatCurrency(item.line_amount)}</td>
                                        <td className="p-3 align-middle">{item.vat_percentage || '0.00'}%</td>
                                        <td className="p-3 align-middle">{formatCurrency(item.vat_amount)}</td>
                                        <td className="p-3 align-middle">{formatCurrency(item.amount_incl_vat)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="10" className="text-center">No items added</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="row mt-4 d-flex justify-content-end">
                    <div className="col-md-4">
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Gross Total:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.gross_total)}</div>
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Discount %:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{data.discount_percentage || '0.00'}%</div>
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Discount Amt:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.discount_amount)}</div>
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Sub Total:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.sub_total)}</div>
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">VAT Amt:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.vat_amount)}</div>
                            </div>
                        </div>
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Net Total:</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.net_total)}</div>
                            </div>
                        </div>

                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Advance :</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.advance_payment)}</div>
                            </div>
                        </div>
                        
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label fw-bold">Payment :</label>
                            <div className="col-lg-6 col-md-6">
                                <div className="form-control-plaintext text-end">{formatCurrency(data.balance - data.advance_payment)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showPrintModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 9999,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        width: '90%',
                        height: '90%',
                        borderRadius: '8px',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            padding: '15px',
                            borderBottom: '1px solid #ddd',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h5 style={{ margin: 0 }}>Print Preview</h5>
                            <div>
                                <button 
                                    className="btn btn-primary btn-sm me-2"
                                    onClick={() => {
                                        const printWindow = window.open('', '_blank');
                                        if (!printWindow) return;
                                        const baseUrl = window.location.origin + (process.env.PUBLIC_URL || '');
                                        const cssHref = `${baseUrl}/css/cashmemo_print.css`;
                                        const doPrint = () => {
                                            const modalContent = document.querySelector('.modal-print-content');
                                            if (modalContent) {
                                                const printContent = modalContent.innerHTML;
                                                const target = printWindow.document.getElementById('print-content');
                                                if (target) target.innerHTML = printContent;
                                            }
                                            printWindow.document.close();
                                            setTimeout(() => {
                                                printWindow.print();
                                                printWindow.close();
                                            }, 100);
                                        };
                                        printWindow.document.write(`
                                            <html>
                                                <head>
                                                    <title>Cash Memo Print</title>
                                                    <link rel="stylesheet" href="${cssHref}" id="print-css-link" />
                                                </head>
                                                <body>
                                                    <div id="print-content"></div>
                                                </body>
                                            </html>
                                        `);
                                        const linkEl = printWindow.document.getElementById('print-css-link');
                                        if (linkEl) {
                                            const fallback = setTimeout(doPrint, 3000);
                                            const done = () => { clearTimeout(fallback); doPrint(); };
                                            linkEl.onload = done;
                                            linkEl.onerror = done;
                                            if (linkEl.sheet) done();
                                        } else {
                                            doPrint();
                                        }
                                    }}
                                >
                                    <i className="fas fa-print me-1"></i>
                                    Print
                                </button>
                                <button 
                                    className="btn btn-secondary btn-sm"
                                    onClick={handleClosePrintModal}
                                >
                                    <i className="fas fa-times me-1"></i>
                                    Close
                                </button>
                            </div>
                        </div>
                        <div style={{
                            height: 'calc(100% - 70px)',
                            overflow: 'auto',
                            padding: '20px'
                        }} className="modal-print-content">

                            <div id="container">
                                <div class="print-content">
                                    <div class="clearfix"></div>
                                    <div className="title">
                                        <span>TAX INVOICE</span>
                                    </div>
                                    <section class="middle-box">
                                        <section id="client-info">
                                            <span class="client-name invoice_client_name">
                                                {getText(data.customer_name) || data.customer?.label || ''}
                                            </span>
                                            <span class="client-address">
                                                {getText(data.address2) || ''}
                                                {data.country?.label || getText(data.country?.name) || ''}
                                            </span>
                                            <section id="client-contacts">
                                                <div>
                                                    <span class="client_label">PHONE : </span>
                                                    <span class="client_value">{getText(data.phone_number) || getText(data.customer?.phone) || '-'}</span>
                                                </div>
                                                <div>
                                                    <span class="client_label">VAT NO : </span>
                                                    <span class="client_value">{getText(data.vat_number) || getText(data.customer?.vat_number) || '-'}</span>
                                                </div>
                                            </section>
                                        </section>

                                        <section id="invoice-info">
                                            <div>
                                                <span class="invoice_label">INVOICE NUMBER : </span>
                                                <span class="invoice_value">{getText(data.invoice_number) || getText(data.number) || '-'}</span>
                                            </div>
                                            <div>
                                                <span class="invoice_label">INVOICE DATE : </span>
                                                <span class="invoice_value">{formatDate(data.invoice_date || data.delivery_date)}</span>
                                            </div>
                                        </section>
                                    </section>
                                    <div class="clearfix"></div>
                                    <section id="items">
                                        {(() => {
                                            const lineItems = Array.isArray(data.items) 
                                                ? data.items : [];
                                            return (
                                                <table cellspacing="0" cellpadding="0">
                                                    <thead>
                                                        <tr>
                                                            <th className=""></th>
                                                            <th className="">ITEM </th>
                                                            <th className="invoice_item_unit_label">UNIT</th>
                                                            <th className="invoice_item_quantity_label">QTY</th>
                                                            <th className="invoice_item_price_label">PRICE</th>
                                                            <th className="invoice_item_vat_label">AMT.EXCL VAT</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {lineItems && lineItems.length > 0 ? (
                                                            lineItems.map((row, idx) => {
                                                                const itemCode = getText(row.item?.item_code) || getText(row.item_code) || '-';
                                                                const itemName = getText(row.item_description) || '-';
                                                                const unit = getText(row.unit) || getText(row.item?.unit) || '-';
                                                                const qty = row.quantity || row.qty || 0;
                                                                const unitPrice = parseFloat(row.unit_price || 0);
                                                                const discountPercent = parseFloat(row.discount_percentage || 0);
                                                                const vatPercent = parseFloat(row.vat_percentage || 0);

                                                                const lineTotal = qty * unitPrice;
                                                                const discountAmount = (lineTotal * discountPercent) / 100;
                                                                const amountExclVat = lineTotal - discountAmount;
                                                                const vatAmount = (amountExclVat * vatPercent) / 100;
                                                                const amountInclVat = amountExclVat + vatAmount;

                                                                return (
                                                                    <tr key={idx} data-iterate="item">
                                                                        <td className="invoice_item_row_number" style={{ position: 'relative' }}>
                                                                            {idx + 1}
                                                                        </td>
                                                                        <td>
                                                                            <span className="">
                                                                                <b>{itemName}</b>
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            <span className="show-mobile invoice_item_unit_label" style={{ display: 'inline' }}
                                                                                contenteditable="true">UNIT</span>
                                                                            <span className="invoice_item_unit" contenteditable="true">{unit}</span>
                                                                        </td>
                
                                                                        <td>
                                                                            <span className="show-mobile invoice_item_quantity_label" style={{ display: 'inline' }}
                                                                                contenteditable="true">QUANTITY</span>
                                                                            <span className="invoice_item_quantity" contenteditable="true">{qty}</span>
                                                                        </td>
                                                                        <td>
                                                                            <span className="show-mobile invoice_item_price_label" style={{ display: 'inline' }}
                                                                                contenteditable="true">PRICE :
                                                                            </span>
                                                                            <span className="invoice_item_price" contenteditable="true">{formatCurrency(unitPrice)}</span>
                                                                        </td>
                                                                        <td>
                                                                            <span className="show-mobile invoice_item_vat_label" style={{ display: 'inline' }}
                                                                                contenteditable="true">AMT.EXCL VAT:
                                                                            </span>
                                                                            <span className="invoice_item_vat " contenteditable="true">{formatCurrency(amountExclVat)}</span>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="10" style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>No items available</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            );
                                        })()}
                                    </section>
                                    <div class="clearfix"></div>
                                    <section id="sums">
                                        <table cellspacing="0" cellpadding="0">
                                            <tbody>
                                                <tr className="amount-total">
                                                    <td rowSpan={5} className="invoice_amount_in_words">
                                                        {formatAmountInWords(data.net_total)}
                                                    </td>
                                                    <td className="invoice_amount_subtotal_label " contenteditable="true">
                                                        GROSS TOTAL
                                                    </td>
                                                    <td className="invoice_amount_subtotal ">{formatCurrency(data.gross_total)}</td>
                                                </tr>
                                                <tr className="amount-total">
                                                    <td className="invoice_amount_subtotal_label " contenteditable="true">
                                                        DISCOUNT
                                                    </td>
                                                    <td className="invoice_amount_subtotal ">{formatCurrency(data.discount_amount)}</td>
                                                </tr>
                                                <tr className="amount-total">
                                                    <td className="invoice_amount_subtotal_label " contenteditable="true">
                                                        SUB TOTAL
                                                    </td>
                                                    <td className="invoice_amount_subtotal ">{formatCurrency(data.sub_total)}</td>
                                                </tr>
                                                <tr className="amount-total">
                                                    <td className="invoice_amount_subtotal_label">VAT 10%</td>
                                                    <td className="invoice_amount_subtotal bold">{formatCurrency(data.vat_amount)}</td>
                                                </tr>
                                                <tr className="amount-total">
                                                    <td className="invoice_amount_subtotal_label bold">NET TOTAL</td>
                                                    <td className="invoice_amount_subtotal bold">{formatCurrency(data.net_total)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </section>
                                </div>
                            </div>


                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CashMemoPreview;
